import { AxleCallout } from '@/components/displayFields/AxleCallout';
import { AxleCheckboxFieldSimple } from '@/components/formFields/checkboxes';
import { AxleMomentTimeOfDayField } from '@/components/formFields/datetimeFields';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { NotesInput } from '@/components/notes/NotesDisplay';
import { NotesInfoIcon } from '@/components/notes/NotesInfoIcon';
import { If } from '@/components/utils/conditionalComponents';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { formatTimeRangeFromStrings } from '@/utils/datetimes/formatting';
import {
    DayOfWeekAbbr,
    WeekdayTimeWindow,
    dayOfWeekAbbrToIndex,
    daysOfWeek,
    indexToDayOfWeekAbbr,
    renderFullWeekdayNameFromAbbr,
} from '@/utils/datetimes/weekdays';
import { useInputState } from '@/utils/inputState';
import { useConfigValue } from '@/utils/useConfigValue';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { Delete } from '@mui/icons-material';
import { Box, IconButton, Link, Typography } from '@mui/material';
import moment from 'moment';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { errorToast } from '../../toast';
import { EditCancelButtons, patientFieldStyles } from './PatientDetailsPage.common';

const SectionLayout = ({
    title,
    editButtons,
    children,
}: {
    title: string;
    editButtons?: React.ReactNode;
    children: React.ReactNode;
}) => (
    <Box
        display='grid'
        gridTemplateColumns='250px 1fr'
        gap={4}
        p={2}
        sx={{ border: '1px solid #E0E0E0' }}
    >
        <Typography variant='h3' lineHeight={'24px'} fontWeight={500} mb={1}>
            {title}
        </Typography>
        {editButtons && <Box sx={{ justifySelf: 'end' }}>{editButtons}</Box>}
        {children}
    </Box>
);

// consts
const TimeSlotFieldStyles = {
    ...patientFieldStyles,
    width: '150px',
};

const isValidTimeWindow = (startTime: string, endTime: string): boolean => {
    const start = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');
    return end.isAfter(start);
};

// types
type TimeSlotFieldsProps = {
    timeSlot: WeekdayTimeWindow;
    dayAbbr: DayOfWeekAbbr;
    timeSlotIndex: number;
    onUpdate: (
        day: DayOfWeekAbbr,
        timeSlotIndex: number,
        field: 'startTime' | 'endTime',
        value: string,
    ) => void;
    onRemove: (day: DayOfWeekAbbr, timeSlotIndex: number) => void;
};

type TimeSlotsByDay = Record<DayOfWeekAbbr, WeekdayTimeWindow[]>;

// components
const TimeSlotFields = ({
    timeSlot,
    dayAbbr,
    timeSlotIndex,
    onUpdate,
    onRemove,
}: TimeSlotFieldsProps) => {
    const startTimeState = useInputState(moment(timeSlot.startTime, 'HH:mm'));
    const endTimeState = useInputState(moment(timeSlot.endTime, 'HH:mm'));

    const handleTimeChange = (field: 'startTime' | 'endTime', newTime: moment.Moment | null) => {
        if (newTime) {
            onUpdate(dayAbbr, timeSlotIndex, field, newTime.format('HH:mm'));
        }
    };

    const isValid = isValidTimeWindow(timeSlot.startTime, timeSlot.endTime);

    return (
        <VerticalStack spacing={1}>
            <HorizontalStack spacing={2} alignItems='center'>
                <Box sx={{ minWidth: '120px' }}>
                    <AxleMomentTimeOfDayField
                        label='Time range'
                        inlineLabel={true}
                        enabled={true}
                        hideHelperText={true}
                        inputState={{
                            ...startTimeState,
                            setInputState: (value) => {
                                const newValue =
                                    typeof value === 'function'
                                        ? value(startTimeState.value)
                                        : value;
                                if (newValue) {
                                    startTimeState.setInputState(newValue);
                                    handleTimeChange('startTime', newValue);
                                }
                            },
                        }}
                        nullable={false}
                        timezone={moment.tz.guess()}
                        intervalMinutes={15}
                        _autocompleteRootInputStyles={TimeSlotFieldStyles}
                    />
                </Box>
                <Typography>to</Typography>
                <Box sx={{ minWidth: '120px' }}>
                    <AxleMomentTimeOfDayField
                        label='Time range'
                        inlineLabel={true}
                        enabled={true}
                        hideHelperText={true}
                        inputState={{
                            ...endTimeState,
                            setInputState: (value) => {
                                const newValue =
                                    typeof value === 'function' ? value(endTimeState.value) : value;
                                if (newValue) {
                                    endTimeState.setInputState(newValue);
                                    handleTimeChange('endTime', newValue);
                                }
                            },
                        }}
                        nullable={false}
                        timezone={moment.tz.guess()}
                        intervalMinutes={15}
                        _autocompleteRootInputStyles={TimeSlotFieldStyles}
                    />
                </Box>
                <IconButton
                    onClick={() => onRemove(dayAbbr, timeSlotIndex)}
                    size='small'
                    color='secondary'
                >
                    <Delete />
                </IconButton>
            </HorizontalStack>
            {!isValid && (
                <AxleCallout type='warning'>End time must be after start time.</AxleCallout>
            )}
        </VerticalStack>
    );
};

const PATIENT_PREFERENCES_FRAGMENT = gql`
    fragment PatientPreferences on Patient {
        id
        preferredTimeslots {
            day
            startTime
            endTime
        }
    }
`;

export const PATIENT_NOTES_FRAGMENT = gql`
    fragment PatientNotes on Patient {
        id
        notes
    }
`;

const PATIENT_PREFERENCES_QUERY = gql`
    query PatientPreferencesQuery($patientId: UUID!) {
        patient(id: $patientId) {
            permissions {
                id
                editNotes
                editAvailability
            }
            ...PatientPreferences
            ...PatientNotes
        }
    }
    ${PATIENT_PREFERENCES_FRAGMENT}
    ${PATIENT_NOTES_FRAGMENT}
`;

const SET_PATIENT_TIMESLOT_PREFERENCES = gql`
    mutation SetPatientTimeslotPreferences($input: SetPatientTimeslotPreferencesInput!) {
        setPatientTimeslotPreferences(input: $input) {
            ...PatientPreferences
        }
    }
    ${PATIENT_PREFERENCES_FRAGMENT}
`;

type SetPatientTimeslotPreferencesMutation = {
    setPatientTimeslotPreferences: {
        id: string;
        preferredTimeslots: WeekdayTimeWindow[];
    };
};

// functions
const mapPreferredTimeslotsByDay = (
    preferredTimeslots: WeekdayTimeWindow[],
): Record<DayOfWeekAbbr, string[]> => {
    const result: Record<string, string[]> = {};
    for (const timeslot of preferredTimeslots) {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!result[timeslot.day]) {
            result[timeslot.day] = [];
        }
        result[timeslot.day].push(formatTimeRangeFromStrings(timeslot.startTime, timeslot.endTime));
    }
    return result;
};

const initializeStateFromTimeslots = (preferredTimeslots: WeekdayTimeWindow[]) => {
    const newSelectedDays = new Set<DayOfWeekAbbr>();
    const newTimeSlots: TimeSlotsByDay = {
        MON: [],
        TUE: [],
        WED: [],
        THU: [],
        FRI: [],
        SAT: [],
        SUN: [],
    };

    preferredTimeslots.forEach((timeslot) => {
        newSelectedDays.add(timeslot.day);
        newTimeSlots[timeslot.day].push(timeslot);
    });

    return { newSelectedDays, newTimeSlots };
};

// inspection found that the format should include the seconds in the time values
const cleanedTimeslots = (timeslots: WeekdayTimeWindow[]): WeekdayTimeWindow[] =>
    timeslots.map((timeslot) => ({
        day: timeslot.day,
        startTime:
            timeslot.startTime.includes(':') && timeslot.startTime.split(':').length === 2
                ? `${timeslot.startTime}:00`
                : timeslot.startTime,
        endTime:
            timeslot.endTime.includes(':') && timeslot.endTime.split(':').length === 2
                ? `${timeslot.endTime}:00`
                : timeslot.endTime,
    }));

const PatientVisitPreferences = ({ patient }: { patient: IPatientPreferences }) => {
    const { preferredTimeslots } = patient;
    const [isEditing, setIsEditing] = useState(false);
    const [setPatientTimeslotPreferences] = useMutation<SetPatientTimeslotPreferencesMutation>(
        SET_PATIENT_TIMESLOT_PREFERENCES,
    );
    const setThisPatientTimeslotPreferences = async (timeslots: WeekdayTimeWindow[]) => {
        await setPatientTimeslotPreferences({
            variables: {
                input: {
                    patientId: patient.id,
                    timeslots: cleanedTimeslots(timeslots),
                },
            },
        });
    };

    const timeslotPreferencesState = useInputState<WeekdayTimeWindow[]>(
        preferredTimeslots.map(({ day, startTime, endTime }) => ({ day, startTime, endTime })),
    );

    const { newSelectedDays: selectedDays, newTimeSlots: timeSlots } = initializeStateFromTimeslots(
        timeslotPreferencesState.value,
    );

    const onCancel = () => {
        setIsEditing(false);
        timeslotPreferencesState.reset();
    };

    const hasInvalidTimeRanges = timeslotPreferencesState.value.some(
        (timeslot) => !isValidTimeWindow(timeslot.startTime, timeslot.endTime),
    );

    const onSubmit = () => {
        if (!timeslotPreferencesState.checkValid()) return;
        if (!timeslotPreferencesState.isDifferent) return;
        if (hasInvalidTimeRanges) {
            errorToast('Please fix invalid time ranges before saving.');
            return;
        }

        void setThisPatientTimeslotPreferences(timeslotPreferencesState.value).then(() => {
            setIsEditing(false);
        });
    };

    const toggleDay = (day: DayOfWeekAbbr) => {
        if (selectedDays.has(day)) {
            const updatedTimeslots = timeslotPreferencesState.value.filter(
                (timeslot) => timeslot.day !== day,
            );
            timeslotPreferencesState.setInputState(updatedTimeslots);
        } else {
            const newTimeSlot: WeekdayTimeWindow = {
                day,
                startTime: '09:00:00',
                endTime: '17:00:00',
            };
            timeslotPreferencesState.setInputState([
                ...timeslotPreferencesState.value,
                newTimeSlot,
            ]);
        }
    };

    const addTimeSlot = (day: DayOfWeekAbbr) => {
        const newTimeSlot: WeekdayTimeWindow = {
            day,
            startTime: '09:00:00',
            endTime: '17:00:00',
        };
        timeslotPreferencesState.setInputState([...timeslotPreferencesState.value, newTimeSlot]);
    };

    const removeTimeSlot = (day: DayOfWeekAbbr, timeSlotIndex: number) => {
        const updatedTimeslots = timeslotPreferencesState.value.filter((ts, index) => {
            if (ts.day === day) {
                const dayIndex = timeslotPreferencesState.value
                    .slice(0, index)
                    .filter((t) => t.day === day).length;
                return dayIndex !== timeSlotIndex;
            }
            return true;
        });
        timeslotPreferencesState.setInputState(updatedTimeslots);
    };

    const updateTimeSlot = (
        day: DayOfWeekAbbr,
        timeSlotIndex: number,
        field: 'startTime' | 'endTime',
        value: string,
    ) => {
        const updatedTimeslots = timeslotPreferencesState.value.map((ts, index) => {
            if (ts.day === day) {
                const dayIndex = timeslotPreferencesState.value
                    .slice(0, index)
                    .filter((t) => t.day === day).length;
                if (dayIndex === timeSlotIndex) {
                    return { ...ts, [field]: value };
                }
            }
            return ts;
        });
        timeslotPreferencesState.setInputState(updatedTimeslots);
    };

    return (
        <SectionLayout
            title='Preferences'
            editButtons={
                patient.permissions.editAvailability ? (
                    <EditCancelButtons
                        isEditing={isEditing}
                        onCancel={onCancel}
                        onSubmit={onSubmit}
                        onEdit={() => setIsEditing(true)}
                        disableSave={
                            !timeslotPreferencesState.isDifferent ||
                            !timeslotPreferencesState.checkValid()
                        }
                    />
                ) : undefined
            }
        >
            {isEditing ? (
                <Box>
                    <Typography component='h2' variant='h3' lineHeight={'24px'} fontWeight={700}>
                        Availability
                    </Typography>
                    <VerticalStack spacing={4}>
                        {daysOfWeek.map((dayName, index) => {
                            const dayAbbr = indexToDayOfWeekAbbr[index];
                            const isSelected = selectedDays.has(dayAbbr);
                            const dayTimeSlots = timeSlots[dayAbbr];

                            return (
                                <HorizontalStack
                                    key={dayAbbr}
                                    spacing={2}
                                    alignItems={dayTimeSlots.length > 0 ? 'flex-start' : 'center'}
                                >
                                    <Box sx={{ minWidth: '150px' }}>
                                        <AxleCheckboxFieldSimple
                                            label={dayName}
                                            value={isSelected}
                                            setValue={() => toggleDay(dayAbbr)}
                                        />
                                    </Box>

                                    {isSelected && (
                                        <VerticalStack spacing={2} sx={{ ml: 4 }}>
                                            {dayTimeSlots.map(
                                                (timeSlot: WeekdayTimeWindow, index: number) => (
                                                    <TimeSlotFields
                                                        key={`${dayAbbr}-${index}`}
                                                        timeSlot={timeSlot}
                                                        dayAbbr={dayAbbr}
                                                        timeSlotIndex={index}
                                                        onUpdate={updateTimeSlot}
                                                        onRemove={removeTimeSlot}
                                                    />
                                                ),
                                            )}
                                            <Link
                                                component='button'
                                                variant='body2'
                                                onClick={() => addTimeSlot(dayAbbr)}
                                                sx={{
                                                    textAlign: 'left',
                                                    color: 'primary.main',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        textDecoration: 'underline',
                                                    },
                                                }}
                                            >
                                                Add Additional Times
                                            </Link>
                                        </VerticalStack>
                                    )}
                                </HorizontalStack>
                            );
                        })}
                    </VerticalStack>
                </Box>
            ) : (
                <>
                    <If condition={timeslotPreferencesState.value.length > 0}>
                        <VerticalStack spacing={3} alignItems='flex-start'>
                            {Object.entries(
                                mapPreferredTimeslotsByDay(timeslotPreferencesState.value),
                            )
                                .sort(
                                    ([a], [b]) =>
                                        dayOfWeekAbbrToIndex[a as DayOfWeekAbbr] -
                                        dayOfWeekAbbrToIndex[b as DayOfWeekAbbr],
                                )
                                .map(([day, timeslots]) => (
                                    <VerticalStack key={day}>
                                        {renderFullWeekdayNameFromAbbr(day as DayOfWeekAbbr)}
                                        {timeslots.map((timeslot, index) => (
                                            <VerticalStack key={`${day}-${index}`}>
                                                {timeslot}
                                            </VerticalStack>
                                        ))}
                                    </VerticalStack>
                                ))}
                        </VerticalStack>
                    </If>
                    <If condition={timeslotPreferencesState.value.length === 0}>
                        <NoneLabel />
                    </If>
                </>
            )}
        </SectionLayout>
    );
};

export const EDIT_PATIENT_NOTES_MUTATION = gql`
    mutation EditPatientNotes($patientId: UUID!, $notes: String!) {
        editPatientNotes(input: { patientId: $patientId, notes: $notes }) {
            ...PatientNotes
        }
    }
    ${PATIENT_NOTES_FRAGMENT}
`;

const PatientAdditionalNotes = ({ patient }: { patient: IPatientPreferences }) => {
    const showPatientNotes = useConfigValue('patient.notes');
    const [isEditing, setIsEditing] = useState(false);
    const notesState = useInputState<string>(patient.notes ?? '');

    const [editPatientNotes] = useMutation(EDIT_PATIENT_NOTES_MUTATION, {
        onCompleted: () => {
            setIsEditing(false);
        },
    });

    if (!showPatientNotes) {
        // Doing this in here since other notes in the future may have different logic
        // For now, its only the patient notes (aka Scheduler Notes) so just return null
        return null;
    }
    const onCancel = () => {
        setIsEditing(false);
        notesState.reset();
    };
    const onSubmit = () => {
        if (!notesState.isValid) return;
        if (!notesState.isDifferent) return;

        void editPatientNotes({
            variables: {
                patientId: patient.id,
                notes: notesState.value,
            },
        });
    };

    return (
        <>
            <SectionLayout
                title='Additional Notes'
                editButtons={
                    patient.permissions.editNotes ? (
                        <EditCancelButtons
                            isEditing={isEditing}
                            onCancel={onCancel}
                            onSubmit={onSubmit}
                            onEdit={() => setIsEditing(true)}
                            disableSave={!notesState.isDifferent || !notesState.isValid}
                        />
                    ) : undefined
                }
            >
                <VerticalStack spacing={1} mt={'-4px'}>
                    <NotesInfoIcon
                        visibleToClinicians={false}
                        headline='Notes about the patient'
                        description='These notes will not be visible to clinicians; they will only be shown in the dashboard.'
                    >
                        <Typography
                            component='h3'
                            variant='body1'
                            lineHeight={'20px'}
                            fontWeight={700}
                            fontSize={'14px'}
                        >
                            Patient Notes (Office Only)
                        </Typography>
                    </NotesInfoIcon>
                    <NotesInput
                        notes={notesState}
                        readOnly={!isEditing}
                        sx={{ backgroundColor: '#fff' }}
                    />
                </VerticalStack>
            </SectionLayout>
        </>
    );
};

interface IPatientPreferences {
    id: string;
    notes: string | null;
    permissions: {
        id: string;
        editNotes: boolean;
        editAvailability: boolean;
    };
    preferredTimeslots: WeekdayTimeWindow[];
}

export const PatientPreferencesTab = () => {
    const { patientId } = useParams<{ patientId: string }>();
    const {
        data: { patient },
    } = useSuspenseQuery<{ patient?: IPatientPreferences }>(PATIENT_PREFERENCES_QUERY, {
        variables: { patientId },
    });

    if (!patient) return null;

    return (
        <VerticalStack gap={3}>
            <PatientVisitPreferences patient={patient} />
            <PatientAdditionalNotes patient={patient} />
        </VerticalStack>
    );
};
